import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, withCsrfHeader } from "@/lib/queryClient";
import { Telescope, Search, Loader2, Trash2, Download, Star, MapPin, Phone, Globe, ExternalLink, AlertTriangle, CheckCircle2, XCircle, X, RefreshCw, Building2, ChevronsUpDown, Check, Coins, Plus, Sparkles, Mail, Save, Play, Linkedin, ChevronDown, Ban, HelpCircle, Instagram, Link2, Store, FileText } from "lucide-react";
import type { LeadGenSearch, LeadGenSandboxItem, Region, ClientTypeRecord } from "@shared/schema";
import { QualificationSettingsCard } from "@/components/qualification-settings-card";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { useWorkspace } from "@/hooks/use-workspace";
import { AnalystIntake } from "@/components/lead-gen/analyst-intake";

interface DuplicateMatch {
  sandboxItemId: string;
  businessName: string;
  phone: string;
  matchedFields: ('name' | 'phone' | 'address')[];
  existingRecord: {
    id: string;
    name: string;
    type: 'lead' | 'client';
    phone: string | null;
    address: string | null;
  };
}

interface RepUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

interface LeadGenPageProps {
  effectiveUserId?: string;
}

interface SandboxData {
  searches: LeadGenSearch[];
  items: LeadGenSandboxItem[];
  settings: {
    quadrantTagsEnabled: boolean;
  };
}

export default function LeadGenPage({ effectiveUserId }: LeadGenPageProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Server-side PATCH /api/lead-gen/settings is gated to roles
  // sales_rep | billing | admin; mirror that on the client so users without
  // those roles don't see admin-only controls that would 403 on save.
  const { workspacePrefix } = useWorkspace();
  const { role: effectiveRole, permissions } = useEffectivePermissions();
  const aiSearchAllowed = permissions["leadGen.feature.aiSearch"] === true;
  const canManageQualSettings = effectiveRole === "admin" || effectiveRole === "billing" || effectiveRole === "sales_rep";

  // Search form state
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [radiusMiles, setRadiusMiles] = useState("25");

  // Modal state
  const [importOpen, setImportOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [regionSearchOpen, setRegionSearchOpen] = useState(false);
  const [regionQuery, setRegionQuery] = useState("");
  const [creatingRegion, setCreatingRegion] = useState(false);
  const [newRegionName, setNewRegionName] = useState("");

  // Import form state
  const [importClientType, setImportClientType] = useState("");
  const [importOwnerId, setImportOwnerId] = useState(effectiveUserId || "");
  const [importRegionId, setImportRegionId] = useState("");
  const [skipIds, setSkipIds] = useState<Set<string>>(new Set());

  // Duplicate review state
  type ImportStep = 'form' | 'checking' | 'review';
  const [importStep, setImportStep] = useState<ImportStep>('form');
  const [duplicateMatches, setDuplicateMatches] = useState<DuplicateMatch[]>([]);
  // dupSkipIds = sandbox item IDs the user chose to skip (defaults to all duplicate IDs)
  const [dupSkipIds, setDupSkipIds] = useState<Set<string>>(new Set());
  // Server-authoritative total from the check-duplicates response. The client's
  // activeItems count can be stale (new searches add rows after the dialog opens),
  // so use this as the source of truth for the review step counts.
  const [serverTotalItems, setServerTotalItems] = useState<number | null>(null);

  // Track whether any search is running for polling interval
  const [isPolling, setIsPolling] = useState(false);

  // Lead Credits state
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  // AI Search state
  const [activeTab, setActiveTab] = useState<"grid" | "ai">("grid");
  const [aiBrief, setAiBrief] = useState("");
  const [aiScopeKind, setAiScopeKind] = useState<"city" | "state" | "region" | "national" | "cities" | "custom">("city");
  const [aiScopeValue, setAiScopeValue] = useState("");
  const [aiVenueTypesText, setAiVenueTypesText] = useState("");
  const [aiCapacityMin, setAiCapacityMin] = useState("");
  const [aiCapacityMax, setAiCapacityMax] = useState("");
  const [aiGenres, setAiGenres] = useState("");
  const [aiExclude, setAiExclude] = useState("");
  const [aiMinFitScore, setAiMinFitScore] = useState("3");
  const [aiResultCap, setAiResultCap] = useState("");
  const [aiModel, setAiModel] = useState<"claude-haiku-4-5" | "claude-sonnet-4-5">("claude-haiku-4-5");
  const [aiSaveAs, setAiSaveAs] = useState("");
  const [aiRecurring, setAiRecurring] = useState(false);
  const [aiTargetContact, setAiTargetContact] = useState("");
  const [showRefinements, setShowRefinements] = useState(false);

  // Scout SSE state
  const [scoutRunning, setScoutRunning] = useState(false);
  const [scoutPhaseLabel, setScoutPhaseLabel] = useState("");
  const [scoutProgressPct, setScoutProgressPct] = useState(0);
  const [scoutDone, setScoutDone] = useState<any>(null);

  // Analyst SSE state
  const [analystRunning, setAnalystRunning] = useState(false);
  const [analystPhase, setAnalystPhase] = useState(0);
  const [analystPhaseLabel, setAnalystPhaseLabel] = useState("");
  const [analystPhaseDetail, setAnalystPhaseDetail] = useState("");
  const [analystDone, setAnalystDone] = useState<any>(null);
  const [showAnalystLocation, setShowAnalystLocation] = useState(false);
  const [analystLocationInput, setAnalystLocationInput] = useState("");

  // Per-row AI action loading state
  const [rowBusy, setRowBusy] = useState<Record<string, "enrich" | "email" | "apollo" | null>>({});

  interface CreditsBalance {
    grantBalance: number;
    purchasedBalance: number;
    totalBalance: number;
    pricePerCreditCents: number;
    packs: Array<{ priceCents: number; credits: number; label: string }>;
    ledger: Array<{ id: string; type: string; grantDelta: number; purchasedDelta: number; createdAt: string; notes: string | null }>;
  }
  const { data: credits } = useQuery<CreditsBalance>({ queryKey: ["/api/lead-credits/balance"] });

  // Debounced keyword + location for the estimate query (400ms)
  const estimateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [estimateKeyword, setEstimateKeyword] = useState("");
  const [estimateLocation, setEstimateLocation] = useState("");
  useEffect(() => {
    if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    estimateTimerRef.current = setTimeout(() => {
      setEstimateKeyword(keyword);
      setEstimateLocation(location);
    }, 400);
    return () => {
      if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    };
  }, [keyword, location]);

  // Pre-search estimate: re-fetches as keyword/location/radius changes
  const { data: searchEstimate } = useQuery<{
    gridSize: number;
    cellCount: number;
    minEstimate: number;
    maxEstimate: number;
    avgPerCell: number | null;
    sampleCount: number;
    confidence: "historical" | "bucket" | "wide";
  }>({
    queryKey: ["/api/lead-gen/estimate", radiusMiles, estimateKeyword, estimateLocation],
    queryFn: () => {
      const params = new URLSearchParams({ radiusMiles });
      if (estimateKeyword) params.set("keyword", estimateKeyword);
      if (estimateLocation) params.set("location", estimateLocation);
      return fetch(`/api/lead-gen/estimate?${params.toString()}`).then((r) => r.json());
    },
    staleTime: 60_000,
  });

  const purchaseMutation = useMutation({
    mutationFn: async (priceCents: number) => {
      const res = await apiRequest("POST", "/api/lead-credits/purchase", { priceCents });
      return res.json();
    },
    onSuccess: (data: { url: string }) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err: any) => toast({ title: "Checkout failed", description: err.message, variant: "destructive" }),
  });

  const confirmPurchaseMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await apiRequest("POST", "/api/lead-credits/purchase/confirm", { sessionId });
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Credits added", description: `${data.credits} credits added to your account.` });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-credits/balance"] });
      // Clean URL
      const url = new URL(window.location.href);
      url.searchParams.delete("credit_purchase");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    },
    onError: (err: any) => toast({ title: "Confirmation failed", description: err.message, variant: "destructive" }),
  });

  // Handle Stripe redirect back from credit purchase
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("credit_purchase");
    const sessionId = params.get("session_id");
    if (status === "success" && sessionId) {
      confirmPurchaseMutation.mutate(sessionId);
    } else if (status === "cancelled") {
      toast({ title: "Purchase cancelled", description: "No credits were added." });
      const url = new URL(window.location.href);
      url.searchParams.delete("credit_purchase");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sandbox data (searches + items) — polls while searches are running
  const { data: sandboxData, isLoading: sandboxLoading } = useQuery<SandboxData>({
    queryKey: ["/api/lead-gen/sandbox"],
    refetchInterval: isPolling ? 3000 : false,
  });

  const searches = sandboxData?.searches || [];
  const items = sandboxData?.items || [];
  const quadrantTagsEnabled = sandboxData?.settings?.quadrantTagsEnabled ?? true;
  // Items the user has X'd — shown at the bottom, excluded from import automatically
  const ignoredItems = items.filter((i) => i.ignored);
  const nonIgnoredItems = items.filter((i) => !i.ignored);
  // Local (temporary) skip = frontend-only exclusion on top of DB ignored
  const activeItems = nonIgnoredItems.filter((i) => !skipIds.has(i.id));

  const hasRunning = searches.some((s) => s.status === "PENDING" || s.status === "RUNNING");

  useEffect(() => {
    setIsPolling(hasRunning);
  }, [hasRunning]);

  // Workspace-level result cap set by admin (null = no cap enforced).
  const { data: leadGenSettings } = useQuery<{ aiResultCap: number | null }>({
    queryKey: ["/api/lead-gen/settings"],
    select: (d) => ({ aiResultCap: d.aiResultCap ?? null }),
  });
  const workspaceResultCap = leadGenSettings?.aiResultCap ?? null;

  // Lookup data
  const { data: regions = [] } = useQuery<Region[]>({ queryKey: ["/api/regions"] });
  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({ queryKey: ["/api/client-types"] });
  const { data: users = [] } = useQuery<RepUser[]>({ queryKey: ["/api/users"] });

  // Pre-fill owner on load
  useEffect(() => {
    if (effectiveUserId && !importOwnerId) setImportOwnerId(effectiveUserId);
  }, [effectiveUserId]);

  // Mutations
  const startSearchMutation = useMutation({
    mutationFn: (data: { keyword: string; location: string; radiusMiles: number }) =>
      apiRequest("POST", "/api/lead-gen/search", data),
    onSuccess: () => {
      toast({ title: "Search started", description: "Results will appear as they come in." });
      setKeyword("");
      setLocation("");
      setRadiusMiles("25");
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
    },
    onError: (err: any) => {
      toast({ title: "Search failed", description: err.message, variant: "destructive" });
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/lead-gen/sandbox/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
    },
  });

  const cancelSearchMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/lead-gen/searches/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not remove search", description: err.message, variant: "destructive" });
    },
  });

  const retrySearchMutation = useMutation({
    mutationFn: (search: LeadGenSearch) => apiRequest("POST", "/api/lead-gen/search", {
      keyword: search.keyword,
      location: search.location,
      radiusMiles: search.radiusMiles,
    }),
    onSuccess: () => {
      toast({ title: "Search restarted", description: "We’ll keep adding results as they arrive." });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not restart search", description: err.message, variant: "destructive" });
    },
  });

  const createRegionMutation = useMutation<Region, Error, string>({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/regions", { name });
      return res.json();
    },
    onSuccess: (region) => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      setImportRegionId(region.id);
      setCreatingRegion(false);
      setNewRegionName("");
      setRegionQuery("");
      toast({ title: "Region created", description: `"${region.name}" has been added.` });
    },
    onError: (err: any) => {
      toast({ title: "Failed to create region", description: err.message, variant: "destructive" });
    },
  });

  const clearMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", "/api/lead-gen/sandbox"),
    onSuccess: () => {
      toast({ title: "Sandbox cleared" });
      setClearOpen(false);
      setSkipIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
    },
  });

  const bulkEnrichMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/lead-gen/sandbox/bulk-find-email", {});
      return res.json();
    },
    onSuccess: (data: { processed: number; found: number; creditsCharged: number; message?: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-credits/balance"] });
      toast({
        title: data.processed === 0 ? "Nothing to re-enrich" : "Bulk re-enrich complete",
        description: data.processed === 0
          ? (data.message || "Every staged row already has a contact email.")
          : `${data.found} of ${data.processed} rows got a verified email · ${data.creditsCharged} credits used`,
      });
    },
    onError: (err: any) => {
      if (err?.code === "INSUFFICIENT_CREDITS") toast({ title: "Insufficient credits", description: err.message, variant: "destructive" });
      else toast({ title: "Bulk re-enrich failed", description: err.message, variant: "destructive" });
    },
  });

  const settingsMutation = useMutation({
    mutationFn: (data: { quadrantTagsEnabled: boolean }) =>
      apiRequest("PATCH", "/api/lead-gen/settings", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to update setting", description: err.message, variant: "destructive" });
    },
  });

  // ===== AI Search =====
  interface SavedSearch {
    id: string;
    name: string;
    brief: string;
    scopeJson: any;
    filtersJson: any;
    recurring: boolean;
    lastRunAt: string | null;
    nextRunAt: string | null;
  }
  const { data: savedSearches } = useQuery<SavedSearch[]>({
    queryKey: ["/api/lead-gen/ai/saved-searches"],
  });

  function buildAiPayload() {
    const scopeValueParsed = aiScopeKind === "cities"
      ? aiScopeValue.split(/[,\n]/).map((s) => s.trim()).filter(Boolean)
      : aiScopeValue.trim();
    const venueTypes = aiVenueTypesText.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    const genres = aiGenres.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    const exclude = aiExclude.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    return {
      brief: aiBrief.trim(),
      scope: { kind: aiScopeKind, value: scopeValueParsed },
      filters: {
        venueTypes,
        capacityMin: aiCapacityMin ? parseInt(aiCapacityMin, 10) : undefined,
        capacityMax: aiCapacityMax ? parseInt(aiCapacityMax, 10) : undefined,
        genres,
        exclude,
        minFitScore: aiMinFitScore ? parseInt(aiMinFitScore, 10) : undefined,
        resultCap: aiResultCap ? parseInt(aiResultCap, 10) : undefined,
        targetContact: aiTargetContact.trim() || undefined,
        modelKey: aiModel,
        requireVerified: true,
      },
    };
  }

  const saveSearchMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiRequest("POST", "/api/lead-gen/ai/saved-searches", payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Saved", description: "Search saved for later." });
      setAiSaveAs("");
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/ai/saved-searches"] });
    },
    onError: (err: any) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const runSavedMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/lead-gen/ai/saved-searches/${id}/run`);
      // Server response shape: { savedSearchId, body: { brief, scope, filters, savedSearchId } }
      const wrapper = await res.json();
      const spec = wrapper?.body || {};
      // Scout route now returns SSE — read the stream and resolve on the done event.
      const csrfHeaders = await withCsrfHeader({ "Content-Type": "application/json" });
      const sseRes = await fetch(`${workspacePrefix}/api/lead-gen/ai/search`, {
        method: "POST",
        headers: csrfHeaders,
        credentials: "include",
        body: JSON.stringify({ ...spec, savedSearchId: id }),
      });
      // Immediately refresh so the RUNNING AI history row appears and polling kicks in.
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
      if (!sseRes.ok) {
        const err = await sseRes.json().catch(() => ({}));
        throw new Error((err as any).message || "Search failed");
      }
      const reader = sseRes.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          try {
            const data = JSON.parse(dataLine.slice(6));
            if (data.error) throw new Error(data.error);
            if (data.done) return data;
          } catch (parseErr: any) {
            if (parseErr.message && !parseErr.message.includes("JSON")) throw parseErr;
          }
        }
      }
      throw new Error("Search stream ended without a result");
    },
    onSuccess: (result: any) => {
      toast({
        title: "Saved search ran",
        description: `${result.inserted ?? 0} new leads. ${result.creditsCharged ?? 0} credit${result.creditsCharged === 1 ? "" : "s"} used.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-credits/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/ai/saved-searches"] });
    },
    onError: (err: any) => toast({ title: "Run failed", description: err.message, variant: "destructive" }),
  });

  const deleteSavedMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/lead-gen/ai/saved-searches/${id}`),
    onSuccess: () => {
      toast({ title: "Deleted", description: "Saved search removed." });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/ai/saved-searches"] });
    },
    onError: (err: any) => toast({ title: "Delete failed", description: err.message, variant: "destructive" }),
  });

  function runAiAction(id: string, kind: "enrich" | "email" | "apollo") {
    setRowBusy((p) => ({ ...p, [id]: kind }));
    const path = kind === "enrich"
      ? `/api/lead-gen/sandbox/${id}/enrich`
      : kind === "email"
      ? `/api/lead-gen/sandbox/${id}/find-email`
      : `/api/lead-gen/sandbox/${id}/apollo`;
    apiRequest("POST", path)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        const label = kind === "enrich" ? "Enriched" : kind === "email" ? "Email finder" : "Apollo lookup";
        const detail = body?.email
          ? `Found: ${body.email}`
          : body?.contactName
          ? `Found: ${body.contactName}${body.contactRole ? " — " + body.contactRole : ""}`
          : kind === "enrich"
          ? "Updated AI fields"
          : "No match found";
        toast({ title: label, description: detail });
        queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
        queryClient.invalidateQueries({ queryKey: ["/api/lead-credits/balance"] });
      })
      .catch((err: any) => toast({ title: "Action failed", description: err.message, variant: "destructive" }))
      .finally(() => setRowBusy((p) => ({ ...p, [id]: null })));
  }

  async function runScout() {
    setScoutRunning(true);
    setScoutProgressPct(5);
    setScoutPhaseLabel("🔍 Preparing search…");
    setScoutDone(null);
    try {
      const csrfHeaders = await withCsrfHeader({ "Content-Type": "application/json" });
      const res = await fetch(`${workspacePrefix}/api/lead-gen/ai/search`, {
        method: "POST",
        headers: csrfHeaders,
        credentials: "include",
        body: JSON.stringify(buildAiPayload()),
      });
      // Immediately refresh so the new RUNNING AI history row appears and
      // flips polling on (hasRunning) before we finish reading the SSE stream.
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Scout search failed");
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          try {
            const data = JSON.parse(dataLine.slice(6));
            if (data.phase !== undefined) {
              setScoutPhaseLabel(data.label || "");
              if (data.phase === 1) {
                setScoutProgressPct(20);
              } else if (data.phase === 2) {
                const match = (data.detail || "").match(/(\d+)\s*\/\s*(\d+)/);
                if (match) {
                  const done = parseInt(match[1], 10);
                  const total = parseInt(match[2], 10);
                  const pct = total > 0 ? 20 + (done / total) * 60 : 40;
                  setScoutProgressPct(Math.round(pct));
                } else {
                  setScoutProgressPct(40);
                }
              } else if (data.phase === 3) {
                setScoutProgressPct(90);
              }
            }
            if (data.error) throw new Error(data.error);
            if (data.done) {
              setScoutProgressPct(100);
              setScoutDone(data);
              if (data.reason === "no_verified_candidates") {
                toast({
                  title: "No venues could be verified",
                  description: "Claude found candidates but none matched a real Google Places listing. Try broadening your search or turning off verified-only.",
                  variant: "destructive",
                });
              } else if (data.reason === "no_candidates") {
                toast({
                  title: "No candidates returned",
                  description: "Claude couldn't find matching venues for this brief. Try rewording or widening the scope.",
                  variant: "destructive",
                });
              } else {
                toast({
                  title: "Scout search complete",
                  description: `${data.inserted ?? 0} lead${data.inserted === 1 ? "" : "s"} added · ${data.creditsCharged ?? 0} credit${data.creditsCharged === 1 ? "" : "s"} used`,
                });
              }
              queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
              queryClient.invalidateQueries({ queryKey: ["/api/lead-credits/balance"] });
            }
          } catch (parseErr: any) {
            if (parseErr.message && !parseErr.message.includes("JSON")) throw parseErr;
          }
        }
      }
    } catch (err: any) {
      toast({ title: "Scout search failed", description: err.message, variant: "destructive" });
    } finally {
      setScoutRunning(false);
    }
  }

  function handleAiSearch() {
    if (!aiBrief.trim()) {
      toast({ title: "Brief required", description: "Describe what you're looking for.", variant: "destructive" });
      return;
    }
    if (aiModel === "claude-sonnet-4-5") {
      const hasSpecificLocation = aiScopeKind === "city" || aiScopeKind === "cities";
      if (hasSpecificLocation) {
        runAnalyst(aiScopeValue.trim() || undefined);
      } else {
        const prefill = (aiScopeKind === "state" || aiScopeKind === "region") ? aiScopeValue : "";
        setAnalystLocationInput(prefill);
        setShowAnalystLocation(true);
      }
    } else {
      runScout();
    }
  }

  // `explicitPayload` is the request compiled by the conversational intake.
  // When absent we fall back to buildAiPayload() so the legacy form path is
  // completely unchanged.
  async function runAnalyst(locationOverride?: string, explicitPayload?: any) {
    setAnalystRunning(true);
    setAnalystPhase(1);
    setAnalystPhaseLabel("🗺️ Scouting the landscape...");
    setAnalystPhaseDetail("");
    setAnalystDone(null);
    try {
      const csrfHeaders = await withCsrfHeader({ "Content-Type": "application/json" });
      const res = await fetch(`${workspacePrefix}/api/lead-gen/ai/analyst-search`, {
        method: "POST",
        headers: csrfHeaders,
        credentials: "include",
        body: JSON.stringify(
          explicitPayload
            ? { ...explicitPayload, locationOverride: explicitPayload.locationOverride ?? null }
            : { ...buildAiPayload(), locationOverride: locationOverride || null },
        ),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Analyst search failed");
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          try {
            const data = JSON.parse(dataLine.slice(6));
            if (data.phase !== undefined) {
              setAnalystPhase(data.phase);
              setAnalystPhaseLabel(data.label || "");
              setAnalystPhaseDetail(data.detail || "");
            }
            if (data.error) throw new Error(data.error);
            if (data.done) {
              setAnalystDone(data);
              toast({ title: "Analyst complete!", description: `${data.inserted} leads added · ${data.creditsCharged} credits used` });
              queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
              queryClient.invalidateQueries({ queryKey: ["/api/lead-credits/balance"] });
            }
          } catch (parseErr: any) {
            if (parseErr.message && !parseErr.message.includes("JSON")) throw parseErr;
          }
        }
      }
    } catch (err: any) {
      toast({ title: "Analyst search failed", description: err.message, variant: "destructive" });
    } finally {
      setAnalystRunning(false);
    }
  }

  function handleSaveSearch() {
    if (!aiSaveAs.trim() || !aiBrief.trim()) {
      toast({ title: "Name + brief required", variant: "destructive" });
      return;
    }
    const p = buildAiPayload();
    saveSearchMutation.mutate({
      name: aiSaveAs.trim(),
      brief: p.brief,
      scope: p.scope,
      filters: p.filters,
      recurring: aiRecurring,
      recurringFrequency: aiRecurring ? "monthly" : null,
    });
  }

  const importMutation = useMutation({
    mutationFn: (data: { clientType: string; ownerId: string; regionId: string; skipIds: string[] }) =>
      apiRequest("POST", "/api/lead-gen/import", data),
    onSuccess: (result: any) => {
      toast({
        title: "Leads imported",
        description: `${result.imported} lead${result.imported !== 1 ? "s" : ""} added to the CRM.`,
      });
      setImportOpen(false);
      setImportStep('form');
      setDuplicateMatches([]);
      setDupSkipIds(new Set());
      setSkipIds(new Set());
      setServerTotalItems(null);
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
    },
    onError: (err: any) => {
      toast({ title: "Import failed", description: err.message, variant: "destructive" });
      setImportStep('review');
    },
  });

  const checkDuplicatesMutation = useMutation<{ duplicates: DuplicateMatch[]; totalItems: number }, Error>({
    mutationFn: async () => {
      // Duplicate review must use exactly the same selected rows as the final
      // import. Otherwise items X'd in the sandbox reappear in the "No
      // Duplicates Found" total and look like they will be imported.
      const res = await apiRequest("POST", "/api/lead-gen/check-duplicates", {
        skipIds: Array.from(skipIds),
      });
      return res.json();
    },
    onSuccess: (result) => {
      setDuplicateMatches(result.duplicates);
      setDupSkipIds(new Set(result.duplicates.map((d) => d.sandboxItemId)));
      setServerTotalItems(result.totalItems);
      // Always go to review — even if zero duplicates — so the user can confirm before importing
      setImportStep('review');
    },
    onError: (err: any) => {
      toast({ title: "Check failed", description: err.message, variant: "destructive" });
      setImportStep('form');
    },
  });

  function handleSearch() {
    if (!keyword.trim() || !location.trim()) return;
    const minEst = searchEstimate?.minEstimate ?? 0;
    const balance = credits?.totalBalance ?? 0;
    if (minEst > 0 && balance < minEst) {
      setBuyCreditsOpen(true);
      return;
    }
    startSearchMutation.mutate({ keyword: keyword.trim(), location: location.trim(), radiusMiles: parseInt(radiusMiles, 10) || 25 });
  }

  function handleSkipItem(id: string) {
    setSkipIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleRemoveItem(id: string) {
    removeItemMutation.mutate(id);
  }

  // Step 1: Kick off duplicate check (then either jump to review or import directly)
  function handleCheckAndImport() {
    if (!importClientType || !importRegionId) return;
    setImportStep('checking');
    checkDuplicatesMutation.mutate();
  }

  // Step 3: User confirmed review — import with chosen skips
  function handleReviewImport() {
    const allSkipIds = new Set(Array.from(skipIds).concat(Array.from(dupSkipIds)));
    importMutation.mutate({
      clientType: importClientType,
      ownerId: importOwnerId,
      regionId: importRegionId,
      skipIds: Array.from(allSkipIds),
    });
  }

  function toggleDupSkip(sandboxItemId: string) {
    setDupSkipIds((prev) => {
      const next = new Set(prev);
      next.has(sandboxItemId) ? next.delete(sandboxItemId) : next.add(sandboxItemId);
      return next;
    });
  }

  const totalToImport = activeItems.length;
  const excludedCount = ignoredItems.length + skipIds.size;
  // Use the server's authoritative total (returned by check-duplicates) for the
  // review step. The client's activeItems count can be stale when new searches
  // added sandbox rows after the dialog was opened, causing "30 of 31" to display
  // while the server actually imports 56. Fall back to the client count if the
  // server total hasn't been fetched yet (e.g. on the form step).
  const reviewBaseTotal = serverTotalItems ?? totalToImport;
  const reviewImportCount = reviewBaseTotal - dupSkipIds.size;

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full bg-background">
        {/* Header */}
        <div className="border-b bg-card px-6 py-4">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-3">
              <Telescope className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-semibold">Lead Gen</h1>
              {items.length > 0 && (
                <Badge variant="secondary" className="ml-1">{nonIgnoredItems.length} in sandbox{ignoredItems.length > 0 ? ` · ${ignoredItems.length} excluded` : ""}</Badge>
              )}
            </div>
            {credits && (
              <div className="flex items-center gap-2" data-testid="lead-credits-panel">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-muted/40 cursor-default">
                      <Coins className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium" data-testid="text-credit-balance">
                        {credits.totalBalance.toLocaleString()} credits
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <div className="text-xs space-y-1">
                      <div>Monthly grant: <span className="font-medium">{credits.grantBalance.toLocaleString()}</span> (resets 1st of month)</div>
                      <div>Purchased: <span className="font-medium">{credits.purchasedBalance.toLocaleString()}</span> (rolls over)</div>
                      <div className="text-muted-foreground pt-1 border-t space-y-0.5">
                        <div>Grid search — 1 credit per result</div>
                        <div>AI Scout — 2 credits per lead</div>
                        <div>AI Analyst — 5 credits per lead with a verified email, 2 without</div>
                        <div className="pt-0.5">Charged at search time, not import</div>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
                <Button size="sm" variant="outline" onClick={() => setBuyCreditsOpen(true)} data-testid="button-buy-credits">
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Fuel your pipeline
                </Button>
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Search Google Places to build a lead list. Review, qualify, and import when ready.
            {credits && credits.totalBalance === 0 && (
              <span className="ml-2 text-amber-600 dark:text-amber-500 font-medium">
                You've reached your monthly lead limit — fuel your pipeline to keep importing.
              </span>
            )}
            {credits && credits.totalBalance > 0 && credits.totalBalance < 50 && (
              <span className="ml-2 text-amber-600 dark:text-amber-500 font-medium">
                Running low — top up before your next grid search.
              </span>
            )}
          </p>
        </div>

        {/* Analyst location dialog */}
        <Dialog open={showAnalystLocation} onOpenChange={setShowAnalystLocation}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Where should we search locally?</DialogTitle>
              <DialogDescription>
                Analyst runs targeted local searches in addition to AI discovery. Enter a city, state, or region — or skip to use AI discovery only.
              </DialogDescription>
            </DialogHeader>
            <input
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder='e.g. "Minneapolis, MN" or "Minnesota" or "Dallas, TX"'
              value={analystLocationInput}
              onChange={(e) => setAnalystLocationInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setShowAnalystLocation(false); runAnalyst(analystLocationInput.trim() || undefined); } }}
              autoFocus
              data-testid="input-analyst-location"
            />
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => { setShowAnalystLocation(false); runAnalyst(undefined); }} data-testid="button-analyst-skip-location">
                Skip local search
              </Button>
              <Button onClick={() => { setShowAnalystLocation(false); runAnalyst(analystLocationInput.trim() || undefined); }} data-testid="button-analyst-confirm-location">
                Run Analyst
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Fuel-your-pipeline dialog */}
        <Dialog open={buyCreditsOpen} onOpenChange={setBuyCreditsOpen}>
          <DialogContent data-testid="dialog-buy-credits">
            <DialogHeader>
              <DialogTitle>Fuel Your Pipeline</DialogTitle>
              <DialogDescription>
                Grid search costs 1 credit per result. AI Scout costs 2 credits per lead. AI Analyst costs 5 credits per lead with a verified email, or 2 without one. Credits are charged when results land in your sandbox, not at import time. Topped-up credits roll over indefinitely.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              {credits?.packs.map((pack) => {
                const dollars = (pack.priceCents / 100).toFixed(0);
                const perCredit = (pack.priceCents / pack.credits / 100).toFixed(3);
                return (
                  <button
                    key={pack.priceCents}
                    onClick={() => purchaseMutation.mutate(pack.priceCents)}
                    disabled={purchaseMutation.isPending}
                    className="border rounded-lg p-4 text-left hover:border-primary hover:bg-muted/40 transition disabled:opacity-50"
                    data-testid={`button-pack-${pack.priceCents}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium uppercase text-muted-foreground">{pack.label}</span>
                      <span className="text-lg font-bold">${dollars}</span>
                    </div>
                    <div className="text-2xl font-bold text-primary">{pack.credits.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground mt-1">credits · ${perCredit}/each</div>
                  </button>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setBuyCreditsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="flex-1 overflow-auto">
          <div className="p-6 space-y-6">

            {/* Search Form (Grid + AI tabs) */}
            <Tabs value={aiSearchAllowed ? activeTab : "grid"} onValueChange={(v) => setActiveTab(v as "grid" | "ai")}>
              <TabsList className="mb-3" data-testid="tabs-leadgen-mode">
                <TabsTrigger value="grid" data-testid="tab-grid-search">
                  <Search className="w-3.5 h-3.5 mr-1.5" /> Grid Search
                </TabsTrigger>
                {aiSearchAllowed && (
                  <TabsTrigger value="ai" data-testid="tab-ai-search">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Search
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="grid" className="mt-0">
            <div className="bg-card border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <Search className="w-4 h-4 text-muted-foreground" />
                New Search
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div className="md:col-span-1 space-y-1">
                  <Label htmlFor="lg-keyword" className="text-xs">Business Type / Keyword</Label>
                  <Input
                    id="lg-keyword"
                    placeholder='e.g. "senior living"'
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    data-testid="input-leadgen-keyword"
                  />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label htmlFor="lg-location" className="text-xs">Location</Label>
                  <Input
                    id="lg-location"
                    placeholder='e.g. "Denver, CO" or "Milwaukee, WI"'
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    data-testid="input-leadgen-location"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="lg-radius" className="text-xs">Radius (miles)</Label>
                  <div className="flex gap-2">
                    <Select value={radiusMiles} onValueChange={setRadiusMiles}>
                      <SelectTrigger id="lg-radius" data-testid="select-leadgen-radius">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["5", "10", "15", "25", "35", "50", "75", "100"].map((r) => (
                          <SelectItem key={r} value={r}>{r} mi</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleSearch}
                      disabled={!keyword.trim() || !location.trim() || startSearchMutation.isPending}
                      data-testid="button-leadgen-search"
                      className="shrink-0"
                    >
                      {startSearchMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      <span className="ml-1.5 hidden sm:inline">Search</span>
                    </Button>
                  </div>
                </div>
              </div>
              {/* Pre-search estimate banner */}
              {activeTab === "grid" && (
                searchEstimate?.confidence === "wide" ? (
                  <div className="flex items-center gap-2 mt-3 rounded-md border border-muted bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="banner-search-estimate">
                    <Coins className="w-3.5 h-3.5 shrink-0" />
                    <span>Enter a keyword and location for a tighter estimate.</span>
                  </div>
                ) : searchEstimate ? (
                  <div className={`flex items-center gap-2 mt-3 rounded-md border px-3 py-2 text-xs ${
                    credits && credits.totalBalance < searchEstimate.minEstimate
                      ? "border-destructive/50 bg-destructive/5 text-destructive"
                      : "border-muted bg-muted/40 text-muted-foreground"
                  }`} data-testid="banner-search-estimate">
                    <Coins className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      This search is estimated to return{" "}
                      <strong className="text-foreground">{searchEstimate.minEstimate.toLocaleString()}–{searchEstimate.maxEstimate.toLocaleString()} results</strong>{" "}
                      and cost{" "}
                      <strong className="text-foreground">{searchEstimate.minEstimate.toLocaleString()}–{searchEstimate.maxEstimate.toLocaleString()} credits</strong>
                      {" "}({searchEstimate.cellCount} grid cells × 1 credit/result).
                      {searchEstimate.confidence === "historical" && searchEstimate.sampleCount > 0 && (
                        <span className="ml-1 text-muted-foreground">Based on {searchEstimate.sampleCount} past {searchEstimate.sampleCount === 1 ? "search" : "searches"}.</span>
                      )}
                      {searchEstimate.confidence === "bucket" && (
                        <span className="ml-1 text-muted-foreground">Estimate will tighten after your first search.</span>
                      )}
                    </span>
                    {credits && credits.totalBalance < searchEstimate.minEstimate && (
                      <button
                        className="ml-auto shrink-0 underline underline-offset-2 font-medium"
                        onClick={() => setBuyCreditsOpen(true)}
                        data-testid="link-buy-credits-from-estimate"
                      >
                        Buy credits
                      </button>
                    )}
                  </div>
                ) : null
              )}
              <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                <p className="text-xs text-muted-foreground">
                  Searches run in the background — stack multiple searches to build your list. Results deduplicate automatically. Credits are charged per result, not per import.
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch
                    id="lg-quadrant-toggle"
                    checked={quadrantTagsEnabled}
                    disabled={settingsMutation.isPending}
                    onCheckedChange={(checked) => settingsMutation.mutate({ quadrantTagsEnabled: checked })}
                    data-testid="switch-leadgen-quadrant"
                  />
                  <Label htmlFor="lg-quadrant-toggle" className="text-xs cursor-pointer flex items-center gap-1.5">
                    Tag results by direction
                    <span className="text-muted-foreground font-normal">(NE/NW/SE/SW)</span>
                  </Label>
                </div>
              </div>
            </div>
              </TabsContent>
              <TabsContent value="ai" className="mt-0 space-y-3">
                {/* AI Lead Qualification hidden — parked for future enhancement */}
                <div className="bg-card border rounded-lg p-5 space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-sm font-semibold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        AI-Powered Discovery
                      </h2>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Describe your ideal client. We find, verify, and source contact info automatically.
                      </p>
                    </div>
                    {/* Live cost estimator */}
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold tabular-nums">
                        {(parseInt(aiResultCap, 10) || 25) * (aiModel === "claude-sonnet-4-5" ? 5 : 2)} credits est.
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {parseInt(aiResultCap, 10) || 25} results × {aiModel === "claude-sonnet-4-5" ? 5 : 2} cr/lead
                      </div>
                    </div>
                  </div>

                  {/* Saved searches */}
                  {savedSearches && savedSearches.length > 0 && (
                    <div className="border rounded-md p-3 bg-muted/20 space-y-2">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Saved Searches</Label>
                      <div className="flex flex-wrap gap-2">
                        {savedSearches.map((ss) => (
                          <div key={ss.id} className="flex items-center gap-1 border rounded-md bg-card px-2 py-1" data-testid={`saved-search-${ss.id}`}>
                            <span className="text-xs font-medium">{ss.name}</span>
                            {ss.recurring && <Badge variant="secondary" className="text-[9px] h-4 px-1">monthly</Badge>}
                            <button
                              onClick={() => runSavedMutation.mutate(ss.id)}
                              disabled={runSavedMutation.isPending}
                              className="ml-1 text-primary hover:opacity-80 disabled:opacity-40"
                              title="Run now"
                              data-testid={`button-run-saved-${ss.id}`}
                            >
                              {runSavedMutation.isPending && runSavedMutation.variables === ss.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Play className="w-3 h-3" />}
                            </button>
                            <button
                              onClick={() => { if (confirm(`Delete saved search "${ss.name}"?`)) deleteSavedMutation.mutate(ss.id); }}
                              className="text-muted-foreground hover:text-destructive"
                              title="Delete"
                              data-testid={`button-delete-saved-${ss.id}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Scout / Analyst toggle */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setAiModel("claude-haiku-4-5")}
                      className={`rounded-lg border p-3 text-left transition-colors ${aiModel === "claude-haiku-4-5" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                      data-testid="button-scout"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Scout</div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span onClick={e => e.stopPropagation()} className="text-muted-foreground hover:text-foreground cursor-help">
                              <HelpCircle className="w-3.5 h-3.5" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[240px] text-xs p-3 space-y-1.5">
                            <p className="font-semibold text-foreground">Scout — Fast Discovery</p>
                            <p>AI generates a candidate list, then Google Places verifies each business.</p>
                            <p className="text-muted-foreground">Delivers: name, address, phone, website, and rating. No email or contact person.</p>
                            <p className="font-medium">2 cr/lead · free if no candidates found</p>
                            <p className="text-muted-foreground italic">Tip: cast a wide net with Scout, then Enrich only the winners — 4 cr total vs 5 cr for Analyst with email.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">Fast, broad search · 2 cr/lead</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAiModel("claude-sonnet-4-5")}
                      className={`rounded-lg border p-3 text-left transition-colors ${aiModel === "claude-sonnet-4-5" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                      data-testid="button-analyst"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Analyst</div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span onClick={e => e.stopPropagation()} className="text-muted-foreground hover:text-foreground cursor-help">
                              <HelpCircle className="w-3.5 h-3.5" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[260px] text-xs p-3 space-y-1.5">
                            <p className="font-semibold text-foreground">Analyst — Deep Enrichment</p>
                            <p>8-phase pipeline: AI discovery → Apollo org lookup → marketplace &amp; web search → Hunter email cascade → NeverBounce verification → Google Places confirmation.</p>
                            <p className="text-muted-foreground">Delivers: everything Scout gives + email address, contact name &amp; role.</p>
                            <p className="font-medium">5 cr/lead with email · 2 cr/lead without email</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">8-phase · deep enrichment · 5 cr/lead with email</div>
                    </button>
                  </div>

                  {/* Scout phase progress */}
                  {scoutRunning && (
                    <div className="bg-muted/30 border rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
                        <span className="text-sm font-medium">{scoutPhaseLabel}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className="bg-primary rounded-full h-1.5 transition-all duration-700"
                          style={{ width: `${scoutProgressPct}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>{scoutProgressPct < 100 ? "Running…" : "Done"}</span>
                        <span>{scoutProgressPct}%</span>
                      </div>
                    </div>
                  )}

                  {/* Analyst phase progress */}
                  {analystRunning && (
                    <div className="bg-muted/30 border rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
                        <span className="text-sm font-medium">{analystPhaseLabel}</span>
                      </div>
                      {analystPhaseDetail && (
                        <p className="text-xs text-muted-foreground pl-6">{analystPhaseDetail}</p>
                      )}
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className="bg-primary rounded-full h-1.5 transition-all duration-700"
                          style={{ width: `${(analystPhase / 8) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>Phase {analystPhase} of 8</span>
                        <span>{Math.round((analystPhase / 8) * 100)}%</span>
                      </div>
                    </div>
                  )}

                  {/* Conversational intake — Analyst only. Scout keeps the
                      plain brief box; its whole value is being fast and cheap,
                      and a question ladder would undercut that. */}
                  {aiModel === "claude-sonnet-4-5" && !analystRunning && (
                    <AnalystIntake
                      workspacePrefix={workspacePrefix}
                      resultCap={aiResultCap ? parseInt(aiResultCap, 10) : 25}
                      running={analystRunning}
                      onRun={(request) => runAnalyst(request.locationOverride, request)}
                    />
                  )}

                  {/* Main prompt */}
                  <Textarea
                    id="ai-brief"
                    placeholder='e.g. "Mid-size country clubs in Texas that book regular live music for member events. Capacity 100–300. Avoid wedding-only venues."'
                    value={aiBrief}
                    onChange={(e) => setAiBrief(e.target.value)}
                    rows={3}
                    className="text-sm resize-none"
                    data-testid="textarea-ai-brief"
                  />

                  {/* Result Cap — just below prompt */}
                  <div className="flex items-center gap-3">
                    <Label htmlFor="ai-cap" className="text-xs text-muted-foreground whitespace-nowrap">Result Cap</Label>
                    <Input
                      id="ai-cap"
                      type="number"
                      min="1"
                      max={workspaceResultCap !== null ? workspaceResultCap : 200}
                      placeholder="None"
                      value={aiResultCap}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (workspaceResultCap !== null && parseInt(v, 10) > workspaceResultCap) {
                          setAiResultCap(String(workspaceResultCap));
                        } else {
                          setAiResultCap(v);
                        }
                      }}
                      className="w-28 h-8 text-sm"
                      data-testid="input-ai-cap"
                    />
                    {workspaceResultCap !== null && (
                      <span className="text-xs text-muted-foreground">Workspace limit: {workspaceResultCap}</span>
                    )}
                  </div>

                  {/* Optional refinements — collapsible */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowRefinements((v) => !v)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors select-none"
                      data-testid="button-toggle-refinements"
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRefinements ? "" : "-rotate-90"}`} />
                      {showRefinements ? "Hide refinements" : "Refine search (optional)"}
                    </button>

                    {showRefinements && (
                      <div className="mt-3 space-y-3">
                        {/* Geographic scope */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="ai-scope-kind" className="text-xs">Geographic Scope</Label>
                            <Select value={aiScopeKind} onValueChange={(v) => setAiScopeKind(v as any)}>
                              <SelectTrigger id="ai-scope-kind" data-testid="select-ai-scope-kind">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="city">Single City</SelectItem>
                                <SelectItem value="state">State</SelectItem>
                                <SelectItem value="region">Region (NE/SE/MW/SW/W)</SelectItem>
                                <SelectItem value="cities">Multiple Cities</SelectItem>
                                <SelectItem value="national">National</SelectItem>
                                <SelectItem value="custom">Custom Prompt</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="ai-scope-value" className="text-xs">
                              {aiScopeKind === "national" ? "(no value needed)" : aiScopeKind === "cities" ? "Cities (comma-separated)" : aiScopeKind === "custom" ? "Custom location prompt" : "Value"}
                            </Label>
                            <Input
                              id="ai-scope-value"
                              placeholder={
                                aiScopeKind === "city" ? "Austin, TX" :
                                aiScopeKind === "state" ? "Texas" :
                                aiScopeKind === "region" ? "Southeast" :
                                aiScopeKind === "cities" ? "Austin, Dallas, Houston" :
                                aiScopeKind === "custom" ? "e.g. Gulf Coast resort towns" : ""
                              }
                              value={aiScopeValue}
                              onChange={(e) => setAiScopeValue(e.target.value)}
                              disabled={aiScopeKind === "national"}
                              data-testid="input-ai-scope-value"
                            />
                          </div>
                        </div>

                        {/* Venue type + capacity */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="ai-venue-types" className="text-xs">Business Types</Label>
                            <Input
                              id="ai-venue-types"
                              placeholder="law firm, magician, real estate agency"
                              value={aiVenueTypesText}
                              onChange={(e) => setAiVenueTypesText(e.target.value)}
                              data-testid="input-ai-venue-types"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="ai-cap-min" className="text-xs">Capacity Min</Label>
                            <Input id="ai-cap-min" type="number" placeholder="100" value={aiCapacityMin} onChange={(e) => setAiCapacityMin(e.target.value)} data-testid="input-ai-cap-min" />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="ai-cap-max" className="text-xs">Capacity Max</Label>
                            <Input id="ai-cap-max" type="number" placeholder="300" value={aiCapacityMax} onChange={(e) => setAiCapacityMax(e.target.value)} data-testid="input-ai-cap-max" />
                          </div>
                        </div>

                        {/* Target contact type — passed to Apollo enrich as person title filter */}
                        <div className="space-y-1">
                          <Label htmlFor="ai-target-contact" className="text-xs">Target contact type</Label>
                          <Input
                            id="ai-target-contact"
                            placeholder='e.g. "talent buyer", "CEO", "marketing director"'
                            value={aiTargetContact}
                            onChange={(e) => setAiTargetContact(e.target.value)}
                            data-testid="input-ai-target-contact"
                          />
                        </div>

                      </div>
                    )}
                  </div>

                  {/* Run button */}
                  <div className="flex justify-end pt-2 border-t">
                    <Button
                      onClick={handleAiSearch}
                      disabled={!aiBrief.trim() || scoutRunning || analystRunning}
                      data-testid="button-ai-search"
                    >
                      {(scoutRunning || analystRunning)
                        ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />{analystRunning ? "Analyst running…" : "Scout running…"}</>
                        : <><Sparkles className="w-4 h-4 mr-1.5" />Run AI Search</>}
                    </Button>
                  </div>
                </div>

                {/* Save search — appears only after results load */}
                {(scoutDone !== null || analystDone !== null) && (
                  <div className="bg-card border rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-muted-foreground">Save this search?</span>
                    <Input
                      placeholder="Give it a name…"
                      value={aiSaveAs}
                      onChange={(e) => setAiSaveAs(e.target.value)}
                      className="max-w-[200px] h-8 text-xs"
                      data-testid="input-ai-save-as"
                    />
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        id="ai-recurring-post"
                        checked={aiRecurring}
                        onCheckedChange={(v) => setAiRecurring(!!v)}
                        data-testid="checkbox-ai-recurring"
                      />
                      <Label htmlFor="ai-recurring-post" className="text-xs cursor-pointer">Repeat monthly</Label>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSaveSearch}
                      disabled={!aiSaveAs.trim() || !aiBrief.trim() || saveSearchMutation.isPending}
                      data-testid="button-ai-save-search"
                    >
                      {saveSearchMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      <span className="ml-1.5">Save</span>
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Active Search Progress */}
            {searches.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Search History</h2>
                <div className="space-y-2">
                  {searches.map((s) => {
                    const isAi = (s as any).source === "ai";
                    const pct = s.totalSubSearches > 0
                      ? Math.round((s.completedSubSearches / s.totalSubSearches) * 100)
                      : s.status === "COMPLETE" ? 100 : 0;
                    return (
                      <div key={s.id} className="bg-card border rounded-lg px-4 py-3 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {isAi && (
                              <Badge variant="outline" className="text-purple-600 border-purple-300 bg-purple-50 dark:bg-purple-900/20 shrink-0 text-[10px] px-1.5 py-0">
                                <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                                AI
                              </Badge>
                            )}
                            <span className="text-sm font-medium truncate">
                              {isAi ? `"${s.keyword}"` : `"${s.keyword}" near ${s.location}`}
                            </span>
                            {s.status === "RUNNING" || s.status === "PENDING" ? (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 shrink-0">
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                {s.status === "PENDING" ? "Queued" : "Searching"}
                              </Badge>
                            ) : s.status === "COMPLETE" ? (
                              <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 dark:bg-green-900/20 shrink-0">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                {s.resultCount} added
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="shrink-0">
                                <XCircle className="w-3 h-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                          </div>
                          {isAi && (
                            <p className="text-xs text-muted-foreground truncate">{s.location}</p>
                          )}
                          {(s.status === "RUNNING" || s.status === "PENDING") && s.totalSubSearches > 0 && (
                            <div className="flex items-center gap-2">
                              <Progress value={pct} className="h-1.5 flex-1" />
                              <span className="text-xs text-muted-foreground shrink-0">
                                {s.completedSubSearches}/{s.totalSubSearches} areas
                              </span>
                            </div>
                          )}
                          {s.status === "FAILED" && s.errorMessage && (
                            <p className="text-xs text-destructive mt-0.5">{s.errorMessage}</p>
                          )}
                        </div>
                        {!isAi && (
                          <span className="text-xs text-muted-foreground shrink-0">{s.radiusMiles}mi radius</span>
                        )}
                        {s.status === "FAILED" && s.errorMessage !== "Cancelled by user" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => retrySearchMutation.mutate(s)}
                            disabled={retrySearchMutation.isPending}
                            title="Try this search again"
                          >
                            <RefreshCw className={`w-3.5 h-3.5 ${retrySearchMutation.isPending ? "animate-spin" : ""}`} />
                            <span className="sr-only">Retry search</span>
                          </Button>
                        )}
                        <button
                          onClick={() => cancelSearchMutation.mutate(s.id)}
                          disabled={cancelSearchMutation.isPending}
                          className="ml-1 shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          title={s.status === "PENDING" || s.status === "RUNNING" ? "Cancel search" : "Remove"}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sandbox Toolbar */}
            {items.length > 0 && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{nonIgnoredItems.length}</span> leads in sandbox
                  {excludedCount > 0 && (
                    <span className="text-muted-foreground">· {excludedCount} excluded from import</span>
                  )}
                </div>
                <div className="flex gap-2">
                  {hasRunning && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Searching…
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => bulkEnrichMutation.mutate()}
                    disabled={bulkEnrichMutation.isPending || activeItems.every((i) => i.aiContactEmail || !i.website)}
                    data-testid="button-leadgen-bulk-reenrich"
                  >
                    {bulkEnrichMutation.isPending
                      ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      : <RefreshCw className="w-4 h-4 mr-1.5" />}
                    Re-enrich Emails
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setClearOpen(true)}
                    data-testid="button-leadgen-clear"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" />
                    Clear List
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setImportOpen(true)}
                    disabled={totalToImport === 0}
                    data-testid="button-leadgen-import"
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    Import {totalToImport > 0 ? `${totalToImport} Lead${totalToImport !== 1 ? "s" : ""}` : "Leads"}
                  </Button>
                </div>
              </div>
            )}

            {/* Sandbox Table */}
            {sandboxLoading && items.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading sandbox…
              </div>
            ) : nonIgnoredItems.length === 0 && ignoredItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground text-center gap-3">
                <Telescope className="w-12 h-12 opacity-20" />
                <p className="text-base font-medium">Your sandbox is empty</p>
                <p className="text-sm max-w-sm">
                  Search for businesses above — results will populate here as they come in.
                  Stack multiple searches to build a bigger list before importing.
                </p>
              </div>
            ) : (
              <div className="bg-card border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b">
                      <tr>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground w-8">
                          <span className="sr-only">Actions</span>
                        </th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Business</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Location</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Phone</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Email</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Website</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Rating</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Maps</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground w-8">
                          <span className="sr-only">Remove</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {[...items].sort((a, b) => {
                        // AI rows (with fit score) sort first by fitScore ascending (1 = best);
                        // non-AI rows keep their original ordering after AI rows.
                        const aFit = a.aiFitScore ?? 99;
                        const bFit = b.aiFitScore ?? 99;
                        const aIsAi = !!a.isAiGenerated;
                        const bIsAi = !!b.isAiGenerated;
                        if (aIsAi && bIsAi) return aFit - bFit;
                        if (aIsAi && !bIsAi) return -1;
                        if (!aIsAi && bIsAi) return 1;
                        return 0;
                      }).map((item) => {
                        const isSkipped = skipIds.has(item.id);
                        return (
                          <SandboxRow
                            key={item.id}
                            item={item}
                            isSkipped={isSkipped}
                            isIgnored={item.ignored}
                            busyKind={rowBusy[item.id] || null}
                            onToggleSkip={() => handleSkipItem(item.id)}
                            onRemove={() => handleRemoveItem(item.id)}
                            onEnrich={() => runAiAction(item.id, "enrich")}
                            onFindEmail={() => runAiAction(item.id, "email")}
                            onApollo={() => runAiAction(item.id, "apollo")}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Clear Confirmation */}
        <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear your sandbox?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove all <strong>{items.length} lead{items.length !== 1 ? "s" : ""}</strong> from
                your sandbox along with all search history. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => clearMutation.mutate()}
                disabled={clearMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="button-leadgen-clear-confirm"
              >
                {clearMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Yes, clear everything
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import Modal — multi-step */}
        <Dialog
          open={importOpen}
          onOpenChange={(open) => {
            if (!open) {
              setImportStep('form');
              setDuplicateMatches([]);
              setDupSkipIds(new Set());
              setServerTotalItems(null);
            }
            setImportOpen(open);
          }}
        >
          <DialogContent className={importStep === 'review' ? "max-w-2xl" : "max-w-md"}>

            {/* ── Step 1: Form ─────────────────────────────────────────── */}
            {importStep === 'form' && (
              <>
                <DialogHeader>
                  <DialogTitle>Import {totalToImport} Lead{totalToImport !== 1 ? "s" : ""}</DialogTitle>
                  <DialogDescription>
                    Confirm the details below.{excludedCount > 0 ? ` ${excludedCount} excluded lead${excludedCount !== 1 ? "s" : ""} will be skipped.` : ""} The sandbox will be cleared after import.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                  {excludedCount > 0 && (
                    <div className="flex gap-2 p-3 bg-muted/50 border rounded-lg text-sm text-muted-foreground">
                      <span>{excludedCount} lead{excludedCount !== 1 ? "s" : ""} excluded from this import (X'd or skipped).</span>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label htmlFor="import-client-type">Client Type <span className="text-destructive">*</span></Label>
                    <Select value={importClientType} onValueChange={setImportClientType}>
                      <SelectTrigger id="import-client-type" data-testid="select-import-client-type">
                        <SelectValue placeholder="Select client type…" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientTypes.map((ct) => (
                          <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {users.length > 1 && (
                    <div className="space-y-1.5">
                      <Label htmlFor="import-owner">Assign Owner <span className="text-destructive">*</span></Label>
                      <Select value={importOwnerId} onValueChange={setImportOwnerId}>
                        <SelectTrigger id="import-owner" data-testid="select-import-owner">
                          <SelectValue placeholder="Select owner…" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.firstName || u.lastName || u.email || u.id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label>Region <span className="text-destructive">*</span></Label>
                    <Popover open={regionSearchOpen} onOpenChange={(open) => { setRegionSearchOpen(open); if (!open) setRegionQuery(""); }}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={regionSearchOpen}
                          className="w-full justify-between font-normal"
                          data-testid="select-import-region"
                        >
                          <span className={importRegionId ? "text-foreground" : "text-muted-foreground"}>
                            {importRegionId ? (regions.find((r) => r.id === importRegionId)?.name ?? "Select region…") : "Select region…"}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
                        <Command>
                          <CommandInput
                            placeholder="Search regions…"
                            value={regionQuery}
                            onValueChange={setRegionQuery}
                          />
                          <CommandList>
                            <CommandEmpty className="px-2 py-1.5 text-sm text-muted-foreground">
                              No region found. Use "+ Create new region" below.
                            </CommandEmpty>
                            <CommandGroup>
                              {regions.map((r) => (
                                <CommandItem
                                  key={r.id}
                                  value={r.name}
                                  onSelect={() => {
                                    setImportRegionId(r.id);
                                    setRegionSearchOpen(false);
                                    setRegionQuery("");
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${importRegionId === r.id ? "opacity-100" : "opacity-0"}`} />
                                  {r.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                            <CommandSeparator />
                            <CommandGroup>
                              <CommandItem
                                value="__create_new__"
                                keywords={["create", "new", "region", "add", regionQuery]}
                                onSelect={() => {
                                  setRegionSearchOpen(false);
                                  setNewRegionName(regionQuery.trim());
                                  setCreatingRegion(true);
                                }}
                                className="text-primary cursor-pointer"
                              >
                                <span className="mr-2 text-base leading-none">+</span>
                                Create new region…
                              </CommandItem>
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {/* Inline new-region form */}
                    {creatingRegion && (
                      <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/40">
                        <Input
                          autoFocus
                          placeholder="Region name…"
                          value={newRegionName}
                          onChange={(e) => setNewRegionName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newRegionName.trim()) createRegionMutation.mutate(newRegionName.trim());
                            if (e.key === "Escape") { setCreatingRegion(false); setNewRegionName(""); }
                          }}
                          className="h-8 text-sm"
                          data-testid="input-new-region-name"
                        />
                        <Button
                          size="sm"
                          className="h-8 shrink-0"
                          onClick={() => newRegionName.trim() && createRegionMutation.mutate(newRegionName.trim())}
                          disabled={!newRegionName.trim() || createRegionMutation.isPending}
                          data-testid="button-create-region"
                        >
                          {createRegionMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Create"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 shrink-0"
                          onClick={() => { setCreatingRegion(false); setNewRegionName(""); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                  <Button
                    onClick={handleCheckAndImport}
                    disabled={!importClientType || !importRegionId || !(importOwnerId || effectiveUserId)}
                    data-testid="button-import-next"
                  >
                    Next →
                  </Button>
                </DialogFooter>
              </>
            )}

            {/* ── Step 2: Checking ─────────────────────────────────────── */}
            {importStep === 'checking' && (
              <>
                <DialogHeader>
                  <DialogTitle>Checking for Duplicates…</DialogTitle>
                  <DialogDescription>
                    Fetching phone numbers and comparing against your existing leads and clients.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4 py-10">
                  <Loader2 className="w-10 h-10 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">This may take a moment for large lists.</p>
                </div>
              </>
            )}

            {/* ── Step 3: Review / Confirm ──────────────────────────────── */}
            {importStep === 'review' && (
              <>
                <DialogHeader>
                  {duplicateMatches.length === 0 ? (
                    <>
                      <DialogTitle className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        No Duplicates Found
                      </DialogTitle>
                      <DialogDescription>
                        All {reviewBaseTotal} lead{reviewBaseTotal !== 1 ? "s" : ""} are unique and ready to import. The sandbox will be cleared after import.
                      </DialogDescription>
                    </>
                  ) : (
                    <>
                      <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        {duplicateMatches.length} Potential Duplicate{duplicateMatches.length !== 1 ? "s" : ""} Found
                      </DialogTitle>
                      <DialogDescription>
                        These leads may already exist in your CRM. Uncheck any you want to skip — duplicates are excluded by default.
                      </DialogDescription>
                    </>
                  )}
                </DialogHeader>

                {duplicateMatches.length > 0 && (
                  <>
                    {/* Bulk actions */}
                    <div className="flex items-center justify-between py-1">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">{reviewImportCount}</span> of <span className="font-medium text-foreground">{reviewBaseTotal}</span> leads will be imported
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDupSkipIds(new Set(duplicateMatches.map((d) => d.sandboxItemId)))}
                          data-testid="button-dup-skip-all"
                        >
                          Skip All Duplicates
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDupSkipIds(new Set())}
                          data-testid="button-dup-import-all"
                        >
                          Import All
                        </Button>
                      </div>
                    </div>

                    {/* Duplicate table */}
                    <div className="border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/60 text-muted-foreground">
                          <tr>
                            <th className="w-10 px-3 py-2 text-center font-medium">Import</th>
                            <th className="px-3 py-2 text-left font-medium">Business Name</th>
                            <th className="px-3 py-2 text-left font-medium">Matched On</th>
                            <th className="px-3 py-2 text-left font-medium">Existing Record</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {duplicateMatches.map((dup) => {
                            const willImport = !dupSkipIds.has(dup.sandboxItemId);
                            return (
                              <tr
                                key={dup.sandboxItemId}
                                className={`transition-colors ${willImport ? "bg-background" : "bg-muted/30"}`}
                              >
                                <td className="px-3 py-2.5 text-center">
                                  <input
                                    type="checkbox"
                                    checked={willImport}
                                    onChange={() => toggleDupSkip(dup.sandboxItemId)}
                                    className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                                    data-testid={`checkbox-dup-import-${dup.sandboxItemId}`}
                                  />
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="font-medium">{dup.businessName}</div>
                                  {dup.phone && <div className="text-xs text-muted-foreground">{dup.phone}</div>}
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="flex flex-wrap gap-1">
                                    {dup.matchedFields.map((f) => (
                                      <span
                                        key={f}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                      >
                                        {f.charAt(0).toUpperCase() + f.slice(1)}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="font-medium">{dup.existingRecord.name}</div>
                                  <div className="text-xs text-muted-foreground capitalize">{dup.existingRecord.type}</div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    onClick={() => setImportStep('form')}
                    className="sm:mr-auto"
                    data-testid="button-dup-back"
                  >
                    ← Back
                  </Button>
                  <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                  <Button
                    onClick={handleReviewImport}
                    disabled={importMutation.isPending || reviewImportCount === 0}
                    data-testid="button-dup-confirm-import"
                  >
                    {importMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    ) : (
                      <Download className="w-4 h-4 mr-1.5" />
                    )}
                    Import {reviewImportCount} Lead{reviewImportCount !== 1 ? "s" : ""}
                  </Button>
                </DialogFooter>
              </>
            )}

          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

// ============================================================================
// Sandbox Row (with hover photo preview)
// ============================================================================

interface SandboxRowProps {
  item: LeadGenSandboxItem;
  isSkipped: boolean;
  isIgnored: boolean;
  busyKind: "enrich" | "email" | "apollo" | null;
  onToggleSkip: () => void;
  onRemove: () => void;
  onEnrich: () => void;
  onFindEmail: () => void;
  onApollo: () => void;
}

function DeliverabilityBadge({ status, source, confidence, itemId }: { status?: string | null; source?: string | null; confidence?: string | null; itemId: string }) {
  if (!status || status === "none") return null;
  const s = status.toLowerCase();
  let cfg: { label: string; cls: string; Icon: typeof Check };
  if (confidence === "low") {
    cfg = { label: "MX-only", cls: "text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20", Icon: HelpCircle };
  } else if (s === "valid") {
    cfg = { label: "Valid", cls: "text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20", Icon: CheckCircle2 };
  } else if (s === "catchall" || s === "accept_all" || s === "accept-all") {
    cfg = { label: "Catch-all", cls: "text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20", Icon: AlertTriangle };
  } else if (s === "invalid" || s === "disposable") {
    cfg = { label: s === "disposable" ? "Disposable" : "Invalid", cls: "text-red-700 dark:text-red-400 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20", Icon: Ban };
  } else {
    cfg = { label: "Unknown", cls: "text-muted-foreground border-border bg-muted/40", Icon: HelpCircle };
  }
  const { Icon } = cfg;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center gap-0.5 rounded border px-1 py-0 h-4 text-[10px] leading-none shrink-0 ${cfg.cls}`}
          data-testid={`badge-deliverability-${itemId}`}
        >
          <Icon className="w-2.5 h-2.5" />{cfg.label}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        Deliverability: {cfg.label}{source ? ` · found via ${source}` : ""}
      </TooltipContent>
    </Tooltip>
  );
}

type ContactChannel = { kind: "phone" | "instagram" | "contact_form" | "links" | "marketplace"; value: string; source?: string };

const CHANNEL_META: Record<ContactChannel["kind"], { Icon: typeof Check; label: string; href: (v: string) => string }> = {
  phone: { Icon: Phone, label: "Phone", href: (v) => `tel:${v}` },
  instagram: { Icon: Instagram, label: "Instagram", href: (v) => (v.startsWith("http") ? v : `https://instagram.com/${v.replace(/^@/, "")}`) },
  contact_form: { Icon: FileText, label: "Contact form", href: (v) => (v.startsWith("http") ? v : `https://${v}`) },
  links: { Icon: Link2, label: "Links", href: (v) => (v.startsWith("http") ? v : `https://${v}`) },
  marketplace: { Icon: Store, label: "Marketplace", href: (v) => (v.startsWith("http") ? v : `https://${v}`) },
};

function ContactChannels({ channels, itemId }: { channels?: ContactChannel[] | null; itemId: string }) {
  const list = (channels || []).filter((c) => c && c.kind !== "phone" && c.value);
  if (list.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`channels-${itemId}`}>
      {list.map((c, i) => {
        const baseMeta = CHANNEL_META[c.kind];
        if (!baseMeta) return null;
        const isLinkedIn = c.kind === "links" && /linkedin\.com/i.test(c.value);
        const meta = isLinkedIn ? { ...baseMeta, Icon: Linkedin, label: "LinkedIn" } : baseMeta;
        const { Icon } = meta;
        return (
          <Tooltip key={`${c.kind}-${i}`}>
            <TooltipTrigger asChild>
              <a
                href={meta.href(c.value)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 rounded border border-border bg-muted/40 px-1 py-0 h-4 text-[10px] leading-none text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`channel-${c.kind}-${itemId}`}
              >
                <Icon className="w-2.5 h-2.5" />{meta.label}
              </a>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px] break-all">
              {meta.label}: {c.value}{c.source ? ` · via ${c.source}` : ""}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function ContactReason({ reason, itemId }: { reason?: string | null; itemId: string }) {
  if (!reason) return <span className="text-muted-foreground/40">—</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground/70 cursor-help"
          data-testid={`reason-no-email-${itemId}`}
        >
          <HelpCircle className="w-3 h-3" /> No email
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px]">{reason}</TooltipContent>
    </Tooltip>
  );
}

function SandboxRow({ item, isSkipped, isIgnored, busyKind, onToggleSkip, onRemove, onEnrich, onFindEmail, onApollo }: SandboxRowProps) {
  const [showPhoto, setShowPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  function handleMouseEnter() {
    if (item.photoReference && !photoError) {
      hoverTimeout.current = setTimeout(() => setShowPhoto(true), 300);
    }
  }

  function handleMouseLeave() {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setShowPhoto(false);
  }

  const photoUrl = item.photoReference
    ? `/api/lead-gen/photo?ref=${encodeURIComponent(item.photoReference)}&maxwidth=320`
    : null;

  return (
    <tr
      className={`group transition-colors ${
        isIgnored
          ? "opacity-40 bg-muted/20"
          : isSkipped
          ? "opacity-40 bg-muted/30"
          : item.isDuplicate
          ? "bg-amber-50/50 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20"
          : "hover:bg-muted/30"
      }`}
    >
      {/* Skip toggle — hidden for ignored rows */}
      <td className="px-3 py-2.5 text-center">
        {!isIgnored && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onToggleSkip}
                className="w-4 h-4 rounded border border-muted-foreground/30 flex items-center justify-center hover:border-primary transition-colors"
                data-testid={`checkbox-skip-${item.id}`}
              >
                {isSkipped && <X className="w-3 h-3 text-muted-foreground" />}
              </button>
            </TooltipTrigger>
            <TooltipContent>{isSkipped ? "Include in import" : "Exclude from import"}</TooltipContent>
          </Tooltip>
        )}
      </td>

      {/* Business name with photo hover */}
      <td className="px-4 py-2.5">
        <div
          className="relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-medium leading-tight ${isIgnored ? "line-through text-muted-foreground" : ""}`}>{item.businessName}</span>
            {item.quadrant && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="text-[10px] font-mono px-1.5 py-0 h-4 leading-none shrink-0"
                    data-testid={`badge-quadrant-${item.id}`}
                  >
                    {item.quadrant}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {item.distanceMiles != null
                    ? `${item.distanceMiles} mi ${item.quadrant} of search center`
                    : `${item.quadrant} of search center`}
                </TooltipContent>
              </Tooltip>
            )}
            {item.isDuplicate && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                </TooltipTrigger>
                <TooltipContent>Phone matches an existing lead or client</TooltipContent>
              </Tooltip>
            )}
            {item.isAiGenerated && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="text-[10px] h-4 px-1 leading-none shrink-0 gap-0.5" data-testid={`badge-ai-${item.id}`}>
                      <Sparkles className="w-2.5 h-2.5" />AI
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    {item.aiSourceLabel || "AI Discovery"}
                    {item.aiNotes ? ` — ${item.aiNotes}` : ""}
                  </TooltipContent>
                </Tooltip>
                {item.verificationStatus && (
                  <Badge
                    variant={item.verificationStatus === "verified" ? "outline" : "outline"}
                    className={`text-[10px] h-4 px-1 leading-none shrink-0 ${
                      item.verificationStatus === "verified"
                        ? "text-green-700 border-green-300 bg-green-50 dark:bg-green-900/20 dark:text-green-300"
                        : "text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300"
                    }`}
                    data-testid={`badge-verify-${item.id}`}
                  >
                    {item.verificationStatus === "verified" ? "✓ verified" : "unverified"}
                  </Badge>
                )}
                {typeof item.aiFitScore === "number" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-0.5 shrink-0" data-testid={`fit-${item.id}`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`w-3 h-3 ${
                              // 1=best → 5 stars filled. 5=worst → 1 star filled.
                              n <= 6 - (item.aiFitScore || 5)
                                ? "text-yellow-400 fill-yellow-400"
                                : "text-muted-foreground/30"
                            }`}
                          />
                        ))}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>AI fit score {item.aiFitScore}/5 (1 = best)</TooltipContent>
                  </Tooltip>
                )}
              </>
            )}
            {item.aiContactName && (
              <span className="text-[11px] text-muted-foreground" data-testid={`contact-${item.id}`}>
                · {item.aiContactName}{item.aiContactRole ? `, ${item.aiContactRole}` : ""}
              </span>
            )}
          </div>

          {/* Hover photo */}
          {showPhoto && photoUrl && !photoError && (
            <div className="absolute left-0 top-full z-50 mt-1 shadow-xl rounded-lg overflow-hidden border bg-card w-64">
              <img
                src={photoUrl}
                alt={item.businessName}
                className="w-full object-cover"
                onError={() => { setPhotoError(true); setShowPhoto(false); }}
              />
              <div className="px-3 py-2 text-xs text-muted-foreground truncate">
                {item.businessName}
              </div>
            </div>
          )}
        </div>
      </td>

      {/* Location */}
      <td className="px-4 py-2.5 text-muted-foreground">
        <div className="flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate max-w-[180px]">
            {[item.city, item.state].filter(Boolean).join(", ") || item.address || "—"}
          </span>
        </div>
      </td>

      {/* Phone */}
      <td className="px-4 py-2.5">
        {item.phone ? (
          <div className="flex items-center gap-1 text-muted-foreground">
            <Phone className="w-3.5 h-3.5 shrink-0" />
            <a href={`tel:${item.phone}`} className="hover:text-foreground transition-colors">{item.phone}</a>
          </div>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>

      {/* Email */}
      <td className="px-4 py-2.5">
        {(() => {
          const conf = (item.aiConfidenceJson || {}) as any;
          return (
            <div className="flex flex-col gap-1 min-w-[160px]">
              {item.aiContactEmail ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <a
                    href={`mailto:${item.aiContactEmail}`}
                    className="flex items-center gap-1 text-primary hover:underline truncate max-w-[180px]"
                    data-testid={`link-email-${item.id}`}
                  >
                    <Mail className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{item.aiContactEmail}</span>
                  </a>
                  <DeliverabilityBadge status={conf.emailStatus} source={conf.emailSource} confidence={conf.emailConfidence} itemId={item.id} />
                </div>
              ) : (
                <ContactReason reason={conf.reason} itemId={item.id} />
              )}
              <ContactChannels channels={conf.channels} itemId={item.id} />
            </div>
          );
        })()}
      </td>

      {/* Website */}
      <td className="px-4 py-2.5">
        {item.website ? (
          <a
            href={item.website.startsWith("http") ? item.website : `https://${item.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline truncate max-w-[160px]"
            data-testid={`link-website-${item.id}`}
          >
            <Globe className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{item.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</span>
          </a>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>

      {/* Rating */}
      <td className="px-4 py-2.5">
        {item.googleRating ? (
          <div className="flex items-center gap-1 text-sm">
            <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
            <span className="font-medium">{item.googleRating.toFixed(1)}</span>
            {item.googleRatingsCount && (
              <span className="text-muted-foreground">({item.googleRatingsCount.toLocaleString()})</span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>

      {/* Maps link */}
      <td className="px-4 py-2.5">
        {item.googleMapsUrl ? (
          <a
            href={item.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
            data-testid={`link-maps-${item.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Maps
          </a>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>

      {/* Remove / Restore + per-row AI actions */}
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1.5 justify-end">
          {!isIgnored && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onEnrich}
                    disabled={busyKind !== null}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary disabled:opacity-40"
                    data-testid={`button-enrich-${item.id}`}
                  >
                    {busyKind === "enrich" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>Enrich with AI (1 credit)</TooltipContent>
              </Tooltip>
              {item.website && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={onFindEmail}
                      disabled={busyKind !== null}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary disabled:opacity-40"
                      data-testid={`button-find-email-${item.id}`}
                    >
                      {busyKind === "email" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Find contact email (1 credit)</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onApollo}
                    disabled={busyKind !== null}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary disabled:opacity-40"
                    data-testid={`button-apollo-${item.id}`}
                  >
                    {busyKind === "apollo" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Linkedin className="w-3.5 h-3.5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>Apollo lookup (1 credit)</TooltipContent>
              </Tooltip>
            </>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onRemove}
                className={`transition-opacity text-muted-foreground ${isIgnored ? "opacity-100 hover:text-primary" : "opacity-0 group-hover:opacity-100 hover:text-destructive"}`}
                data-testid={`button-remove-${item.id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{isIgnored ? "Restore to list" : "Exclude from import"}</TooltipContent>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}
